import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  Sheet,
  Chip,
  Stack,
  Modal,
  ModalDialog,
  ModalClose,
  Textarea,
  FormControl,
  FormLabel,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/joy';
import {
  CheckCircle as CheckIcon,
  XCircle as CancelIcon,
  Envelope as EmailIcon,
  User as PersonIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { api } from '../../../lib/api';

interface Feedback {
  id: number;
  name: string;
  email: string;
  message: string;
  resolved: boolean;
  resolved_by?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  resolved_at?: string;
  resolution_notes?: string;
  created_at: string;
  funnel: {
    id: number;
    name: string;
  };
}

export const ReviewFeedback: React.FC = () => {
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const queryClient = useQueryClient();

  // Fetch feedback
  const { data: feedbackList, isLoading } = useQuery({
    queryKey: ['review-feedback'],
    queryFn: async () => {
      const response = await api.get<{ results: Feedback[] }>('/api/marketing/review-feedback/?ordering=-created_at');
      return response.data.results || [];
    },
  });

  // Resolve feedback mutation
  const resolveFeedback = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes: string }) => {
      const response = await api.post(`/api/marketing/review-feedback/${id}/resolve/`, {
        resolution_notes: notes,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-feedback'] });
      setResolveModalOpen(false);
      setSelectedFeedback(null);
      setResolutionNotes('');
    },
  });

  const handleResolve = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setResolveModalOpen(true);
  };

  const submitResolution = () => {
    if (selectedFeedback) {
      resolveFeedback.mutate({ id: selectedFeedback.id, notes: resolutionNotes });
    }
  };

  if (isLoading) {
    return <Typography>Loading feedback...</Typography>;
  }

  const unresolvedCount = feedbackList?.filter(f => !f.resolved).length || 0;

  return (
    <Box>
      {unresolvedCount > 0 && (
        <Alert color="warning" sx={{ mb: 2 }}>
          You have {unresolvedCount} unresolved feedback item{unresolvedCount !== 1 ? 's' : ''} requiring attention.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>Customer Feedback</Typography>
          
          {!feedbackList || feedbackList.length === 0 ? (
            <Typography level="body-md" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              No feedback received yet. Feedback from customers who had a negative experience will appear here.
            </Typography>
          ) : (
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden' }}>
              <Table hoverRow stickyHeader stripe="odd">
                <thead>
                  <tr>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 150 }}>Date</th>
                    <th style={{ width: 200 }}>Customer</th>
                    <th>Feedback</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbackList.map((feedback) => (
                    <tr key={feedback.id}>
                      <td>
                        <Chip
                          size="sm"
                          color={feedback.resolved ? 'success' : 'warning'}
                          startDecorator={feedback.resolved ? <CheckIcon /> : <CancelIcon />}
                        >
                          {feedback.resolved ? 'Resolved' : 'Pending'}
                        </Chip>
                      </td>
                      <td>
                        <Typography level="body-sm">
                          {format(new Date(feedback.created_at), 'MMM dd, yyyy')}
                        </Typography>
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          {format(new Date(feedback.created_at), 'h:mm a')}
                        </Typography>
                      </td>
                      <td>
                        <Stack spacing={0.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PersonIcon size={16} color="var(--joy-palette-text-secondary)" />
                            <Typography level="body-sm" fontWeight="md">
                              {feedback.name}
                            </Typography>
                          </Box>
                          {feedback.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon size={14} color="var(--joy-palette-text-secondary)" />
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {feedback.email}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </td>
                      <td>
                        <Typography level="body-sm" sx={{ 
                          maxWidth: 400, 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}>
                          {feedback.message}
                        </Typography>
                        {feedback.resolved && feedback.resolution_notes && (
                          <Typography level="body-xs" sx={{ color: 'success.main', mt: 0.5 }}>
                            Resolved by {feedback.resolved_by?.first_name} {feedback.resolved_by?.last_name}
                          </Typography>
                        )}
                      </td>
                      <td>
                        {!feedback.resolved && (
                          <Button
                            size="sm"
                            color="primary"
                            onClick={() => handleResolve(feedback)}
                          >
                            Resolve
                          </Button>
                        )}
                        {feedback.resolved && feedback.resolution_notes && (
                          <Tooltip title={feedback.resolution_notes}>
                            <IconButton size="sm" variant="plain">
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          )}
        </CardContent>
      </Card>

      {/* Resolve Modal */}
      <Modal open={resolveModalOpen} onClose={() => setResolveModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 500 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Resolve Feedback
          </Typography>
          
          {selectedFeedback && (
            <Box sx={{ mb: 3 }}>
              <Typography level="body-sm" fontWeight="lg">
                From: {selectedFeedback.name}
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                {selectedFeedback.email}
              </Typography>
              <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                <Typography level="body-sm">
                  {selectedFeedback.message}
                </Typography>
              </Box>
            </Box>
          )}

          <FormControl>
            <FormLabel>Resolution Notes</FormLabel>
            <Textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Describe how this feedback was addressed..."
              minRows={3}
            />
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: 'flex-end' }}>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setResolveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={submitResolution}
              loading={resolveFeedback.isPending}
            >
              Mark as Resolved
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};